import {calculateDurationBetween, calculateDurationSince, Temporal} from "@/shared/utils/datefunctions";
import {BudgetPlanningType, RunningTimeStatus} from "@/shared/timrTypes";
import {AssignedGroup, AssignedUser, RunningProjectTime, TaskUserBudgetInfo} from "@/shared/timr";
import {millisToHours, minutesToMillis} from "@/shared/utils/misc";

type HoursPlannedCallback = (hoursPlanned: number, userHoursPlanned: number) => void;

export type ConsumedBudgetResult = {
	hoursPlanned: number;
	consumed: number;
	plannedPercentage: number;
	consumedPercentage: number;
	exceededPercentage: number;
};

export class BudgetCalculator {

	private static FRACTIONAL_DIGITS = 2;

	private budgetPlanningType = BudgetPlanningType.None;
	private hoursPlanned = 0;
	private hourlyRate = 0;
	private budgetPlanned: number = null;
	private users: AssignedUser[] = [];
	private hoursPlannedManually = 0;

	constructor(private hoursPlannedChangedCallback: HoursPlannedCallback,
	            private hourlyRateChangedCallback: (value: number) => void,
	            private budgetPlannedChangedCallback: (value: number) => void,
	            private userRecordsChangedCallback: () => void,
	            private userRecordsHoursPlannedChangedCallback: HoursPlannedCallback) {

	}

	/**
	 * Calculates the consumed budget for a task.
	 * If a project time is running, the consumed budget is including the running time.
	 *
	 * @param taskUserBudgetInfo budget data
	 * @param runningProjectTime running project time
	 * @returns hoursPlanned, consumed, plannedPercentage, consumedPercentage, exceededPercentage
	 * @public
	 */
	public static calculateConsumedBudget(taskUserBudgetInfo: TaskUserBudgetInfo, runningProjectTime: RunningProjectTime): ConsumedBudgetResult {
		const hoursPlanned = taskUserBudgetInfo.hoursPlanned;

		// no need to subtract the break time from the consumed time, because the duration is already the rounded net duration
		let consumed = taskUserBudgetInfo.duration || 0;

		let durationRunningRecording: Temporal.Duration;
		if (runningProjectTime && (runningProjectTime?.billable || taskUserBudgetInfo.budgetCountNotBillableHours)) {
			if (runningProjectTime.status === RunningTimeStatus.PAUSED) {
				durationRunningRecording = calculateDurationBetween(runningProjectTime.start, runningProjectTime.pausedAt, runningProjectTime.breakTime || 0);
			} else {
				durationRunningRecording = calculateDurationSince(runningProjectTime.start, runningProjectTime.breakTime || 0);
			}
		} else {
			durationRunningRecording = new Temporal.Duration();
		}

		// round consumed duration if ProjectTime rounding is enabled
		const durationRunningMs = durationRunningRecording.total('milliseconds');
		if (globals.roundingModeProjectTime) {
			const roundToMilliseconds = minutesToMillis(globals.roundToMinutesProjectTime);

			switch (globals.roundingModeProjectTime) {
				case "UP":
					consumed += Math.ceil(durationRunningMs / roundToMilliseconds) * roundToMilliseconds;
					break;
				case "DOWN":
					consumed += Math.floor(durationRunningMs / roundToMilliseconds) * roundToMilliseconds;
					break;
				case "HALF_UP":
					consumed += Math.round(durationRunningMs / roundToMilliseconds) * roundToMilliseconds;
					break;
			}
		} else {
			consumed += durationRunningMs;
		}

		const hoursConsumed = millisToHours(consumed);
		let plannedPercentage: number;
		let consumedPercentage: number;
		let exceededPercentage: number;
		if (hoursConsumed > hoursPlanned) {
			plannedPercentage = hoursConsumed === 0 ? 100 : Math.round(hoursPlanned / hoursConsumed * 100);
			consumedPercentage = plannedPercentage;
			exceededPercentage = 100 - plannedPercentage;
		} else {
			plannedPercentage = 100;
			consumedPercentage = hoursPlanned > 0 ? Math.round(hoursConsumed / hoursPlanned * 100) : 100;
			exceededPercentage = 0;
		}
		return {hoursPlanned, consumed, plannedPercentage, consumedPercentage, exceededPercentage};
	}

	public setBudgetPlanningType(budgetPlanningType: BudgetPlanningType) {
		this.budgetPlanningType = budgetPlanningType;
		switch (this.budgetPlanningType) {
			case BudgetPlanningType.TaskHourlyRate:
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.UserHourlyRate:
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.FixedPrice:
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.None:
				this.resetValues();
				break;
		}
	}

	public getBudgetPlanningType(): BudgetPlanningType {
		return this.budgetPlanningType;
	}

	public setHoursPlanned(hoursPlanned: number) {
		this.hoursPlanned = this.round(hoursPlanned);

		switch (this.budgetPlanningType) {
			case BudgetPlanningType.TaskHourlyRate:
				this.calculateBudgetPlanned();
				break;
			case BudgetPlanningType.FixedPrice:
				this.calculateHourlyRate();
				break;
		}
		if (this.hoursPlannedChangedCallback) {
			this.hoursPlannedChangedCallback(this.hoursPlanned, this.hoursPlannedManually);
		}
	}

	public getHoursPlanned(): number {
		return this.hoursPlanned;
	}

	public setHourlyRate(hourlyRate: number) {
		this.hourlyRate = hourlyRate;
		switch (this.budgetPlanningType) {
			case BudgetPlanningType.TaskHourlyRate:
				this.hourlyRate = this.round(hourlyRate);
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.UserHourlyRate:
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.FixedPrice:
				this.hourlyRate = this.round(hourlyRate);
				this.calculateHoursPlanned();
				break;
		}
		if (this.hourlyRateChangedCallback) {
			this.hourlyRateChangedCallback(this.hourlyRate);
		}
	}

	public getHourlyRate(): number {
		return this.hourlyRate;
	}

	public setBudgetPlanned(budgetPlanned: number) {
		this.budgetPlanned = budgetPlanned ? this.round(budgetPlanned) : null;
		switch (this.budgetPlanningType) {
			case BudgetPlanningType.TaskHourlyRate:
				this.calculateHoursPlanned();
				break;
			case BudgetPlanningType.FixedPrice:
				this.calculateHourlyRate();
				break;
		}
		if (this.budgetPlannedChangedCallback) {
			this.budgetPlannedChangedCallback(this.budgetPlanned);
		}
	}

	public getBudgetPlanned(): number {
		return this.budgetPlanned;
	}

	public setUserRecords(userOrGroups: (AssignedUser | AssignedGroup)[]) {
		this.users = userOrGroups.filter(userOrGroup => userOrGroup.elementType === "USER");
		this.updateHoursPlannedPerUser();
	}

	public getUsers(): AssignedUser[] {
		return this.users;
	}

	public invalidateUserRecords() {
		this.updateHoursPlannedPerUser();
		switch (this.budgetPlanningType) {
			case BudgetPlanningType.TaskHourlyRate:
				this.calculateBudgetPlanned();
				break;
			case BudgetPlanningType.UserHourlyRate:
				this.calculateBudgetPlanned();
				break;
			case BudgetPlanningType.FixedPrice:
				this.calculateHourlyRate();
				break;
		}
	}

	public getHoursPlannedManually(): number {
		return this.hoursPlannedManually;
	}

	public reinitialize(hoursPlanned: number, hourlyRate: number, budgetPlanned: number) {
		this.hoursPlanned = this.round(hoursPlanned);
		this.hourlyRate = this.round(hourlyRate);
		this.budgetPlanned = budgetPlanned ? this.round(budgetPlanned) : null;
		this.updateHoursPlannedPerUser();
		if (this.hoursPlannedChangedCallback) {
			this.hoursPlannedChangedCallback(this.hoursPlanned, this.hoursPlannedManually);
		}
		if (this.hourlyRateChangedCallback) {
			this.hourlyRateChangedCallback(this.hourlyRate);
		}
		if (this.budgetPlannedChangedCallback) {
			this.budgetPlannedChangedCallback(this.budgetPlanned);
		}
		if (this.userRecordsChangedCallback) {
			this.userRecordsChangedCallback();
		}
	}

	public resetValues() {
		this.reinitialize(0, 0, null);
	}

	private calculateHoursPlanned() {
		if (this.budgetPlanningType === BudgetPlanningType.UserHourlyRate) {
			if (!this.budgetPlanned) {
				this.hoursPlanned = 0;
			} else {
				let budgetPlannedManuallySum = 0;
				let hourlyRateAutomaticallySum = 0;

				this.users.forEach(user => {
					const hoursPlanned = user.hoursPlanned;
					const hourlyRate = user.hourlyRate;
					if (user.hoursPlanned != null) {
						budgetPlannedManuallySum += hoursPlanned * hourlyRate;
					} else {
						hourlyRateAutomaticallySum += hourlyRate;
					}
				});

				const hoursPerUser = Math.max(0, this.round(
					hourlyRateAutomaticallySum
						? (this.budgetPlanned - budgetPlannedManuallySum) / hourlyRateAutomaticallySum
						: 0
				));

				let hoursPlannedSum = 0;
				let userRecordsChanged = false;

				this.users.forEach(user => {
					if (user.hoursPlanned != null) {
						hoursPlannedSum += user.hoursPlanned;
					} else if (!user.hourlyRate) {
						user.hoursPlanned = 0;
						userRecordsChanged = true;
					} else {
						user.hoursPlanned = 0;
						hoursPlannedSum += hoursPerUser;
						userRecordsChanged = true;
					}
				});

				this.hoursPlanned = this.round(hoursPlannedSum);
				if (userRecordsChanged && this.userRecordsChangedCallback) {
					this.userRecordsChangedCallback();
				}
			}
			this.calculateHourlyRate();

			this.userRecordsHoursPlannedChangedCallback(this.hoursPlanned, this.hoursPlannedManually);
		} else {
			if (!this.hourlyRate || !this.budgetPlanned) {
				this.hoursPlanned = null;
			} else {
				this.hoursPlanned = this.round(this.budgetPlanned / this.hourlyRate);
			}
			this.updateHoursPlannedPerUser();
		}
		if (this.hoursPlannedChangedCallback) {
			this.hoursPlannedChangedCallback(this.hoursPlanned, this.hoursPlannedManually);
		}
	}

	public updateHoursPlannedPerUser() {
		let hoursPlannedManuallySum = 0;
		const usersAutomatically: AssignedUser[] = [];
		this.users.forEach(user => {
			if (user.hoursPlanned != null) {
				hoursPlannedManuallySum += user.hoursPlanned;
			} else {
				usersAutomatically.push(user);
			}
		});

		if (usersAutomatically.length > 0) {
			let hoursPlannedAvailable = Math.max(0, this.hoursPlanned - hoursPlannedManuallySum);
			const hoursPerUser = this.round(hoursPlannedAvailable / usersAutomatically.length);
			for (const user of usersAutomatically) {
				user.hoursPlanned = null;
				hoursPlannedAvailable -= hoursPerUser;
			}

			if (this.userRecordsChangedCallback) {
				this.userRecordsChangedCallback();
			}
		}
		this.hoursPlannedManually = this.round(hoursPlannedManuallySum);

		this.userRecordsHoursPlannedChangedCallback(this.hoursPlanned, this.hoursPlannedManually);

		if (this.budgetPlanningType === BudgetPlanningType.UserHourlyRate) {
			this.calculateBudgetPlanned();
		}
	}

	private calculateHourlyRate() {
		if (this.budgetPlanningType === BudgetPlanningType.UserHourlyRate) {
			this.hourlyRate = this.round(this.hoursPlanned ? this.budgetPlanned / this.hoursPlanned : 0);
		} else {
			if (!this.hoursPlanned || !this.budgetPlanned) {
				this.hourlyRate = 0;
			} else {
				this.hourlyRate = this.round(this.budgetPlanned / this.hoursPlanned);
			}
		}
		if (this.hourlyRateChangedCallback) {
			this.hourlyRateChangedCallback(this.hourlyRate);
		}
	}

	private calculateBudgetPlanned() {
		if (this.budgetPlanningType !== BudgetPlanningType.UserHourlyRate) {
			if (!this.hoursPlanned || !this.hourlyRate) {
				this.budgetPlanned = 0;
			} else {
				this.budgetPlanned = this.round(this.hoursPlanned * this.hourlyRate);
			}
		}
		if (this.budgetPlannedChangedCallback) {
			this.budgetPlannedChangedCallback(this.budgetPlanned);
		}
	}

	private round(value: number): number {
		return value ? +(Math.round(+((value + "e+" + BudgetCalculator.FRACTIONAL_DIGITS))) + "e-" + BudgetCalculator.FRACTIONAL_DIGITS) : 0;
	}
}
